
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { extractOriginalFileName } from "@/utils/fileOperations";
import type { FileObject } from "@supabase/storage-js";

export const useFileOperations = (bucketName: string) => {
  const { toast } = useToast();

  const previewFile = async (file: FileObject) => {
    try {
      const { data } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(file.name, 60 * 60); // 1 hour

      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to preview file",
        variant: "destructive",
      });
    }
  };

  const downloadFile = async (file: FileObject) => {
    try {
      const { data } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(file.name, 60 * 60);

      if (data?.signedUrl) {
        const link = document.createElement('a');
        link.href = data.signedUrl;
        link.download = extractOriginalFileName(file.name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to download file",
        variant: "destructive",
      });
    }
  };

  const deleteFile = async (file: FileObject, onSuccess: () => void) => {
    if (!confirm(`Are you sure you want to delete "${extractOriginalFileName(file.name)}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase.storage
        .from(bucketName)
        .remove([file.name]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "File deleted successfully",
      });

      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to delete file: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  return {
    previewFile,
    downloadFile,
    deleteFile,
  };
};
